import heroImage from "./article_pics/grift_github_lure_repo_dark_cover.jpg";

const article = {
    title: "GRIFT: Hunting GitHub SEO Malware Lures",
    published: "2026-07-31",
    readingTime: "11 min read",
    tags: ["GitHub", "Threat Hunting", "Malware", "tria.ge", "Detection Engineering"],
    summary: "GitHub README Impostor Fakeware Tracker turns GitHub SEO lure research into ranked repository leads, tria.ge validation, environment ready indicators, and repeatable collection that can reduce Mean Time To Detect.",
    favorite: true,
    category: "Threat Hunting",
    image: heroImage,
    repoUrl: "https://github.com/Pb-22/GRIFT",
    content: `
# GRIFT: Hunting GitHub SEO Malware Lures

GitHub README Impostor Fakeware Tracker (GRIFT) turns GitHub SEO lure research into a repeatable hunting workflow. The project starts with fake software delivery patterns, searches GitHub for repositories that match those patterns, ranks the strongest candidates, and preserves the evidence needed for analyst review.

The workflow is built around a practical reporting boundary. GitHub search can surface suspicious repositories, but repository shape alone is not a malware verdict. GRIFT separates repository lure signals from sandbox behavior, tria.ge validation, and final indicator reporting. This separation makes the output useful for downstream conversion scripts that tailor indicators to a defended environment, such as Splunk lookups, SIEM watchlists, firewall block lists, DNS controls, or internal case management formats.

## Lure shape

The common repository pattern is a fake software landing page. The README presents installer, activator, crack, setup, download, or password language instead of normal project documentation. Many examples have low reputation, few contributors, and little source code. Some use GitHub Releases assets. Others push users toward external domains, Telegram accounts, Steam profiles, or similar campaign infrastructure.

![Fake GitHub app lure repository](${require("./article_pics/grift_github_lure_repo_dark_cover.jpg")})

GRIFT converts that lure shape into GitHub searches. Target terms can include app names, product names, acronyms, installer phrases, and download phrases. The current target set covered Audacity, TeamViewer, AutoCAD, Ableton, SQL Server Management Studio, DeepSeek, UltraViewer, SketchUp, Quicken, AnyDesk, PuTTY, FileZilla, VLC, 7 Zip, WinRAR, Notepad ++, OBS Studio, GIMP, Blender, LibreOffice, and PDF converter themes.

Search results become candidates. Candidate scoring then decides which repositories deserve review, enrichment, or sandbox validation.

## Repository scoring

Strong repository leads usually combine several weak signals. A repository with zero stars, one contributor, a thin README, installer language, a release asset, and a password hint carries more hunting value than any one of those signals alone.

![Fake Audacity README with download button and password hint](${require("./article_pics/grift_fake_audacity_download_button_link.png")})

Positive scoring signals include:

- fake app, setup, download, activator, crack, or installer language;
- thin README style landing pages;
- zero or very low stars and forks;
- one or very few contributors;
- GitHub Releases assets or external payload URLs;
- archive passwords or password hints;
- Telegram, Steam profile, or other campaign path indicators;
- product match to the target list.

Suppressor signals reduce noise. GRIFT lowers confidence for official organizations, official domains, mature repositories, normal source trees, high contributor counts, stars, forks, and ambiguous acronym matches without clear product context.

The result is a ranked candidate queue for analyst review.

## Stage 1 and Stage 2

GRIFT has two stages.

Stage 1 searches GitHub, collects repository metadata, reads repository content, extracts download and release links, enriches payload URLs when enabled, and writes a scored candidate report. This stage does not require tria.ge.

Stage 2 handles sandbox context for selected candidates. The recommended full read only run looks up existing tria.ge reports tied to candidate payload URLs, pulls referenced report data, and writes final indicator output. GRIFT does not automatically retrieve tria.ge PCAPNG files. Those captures remain available from the tria.ge report for manual download when packet level evidence or a Wireshark figure is needed.

![GRIFT Script Workflow](${require("./article_pics/grift_github_seo_flow.png")})

Final indicator output stays conservative. tria.ge records browser, operating system, certificate, CDN, redirect, and platform traffic alongside malicious behavior. GRIFT promotes high scoring files, payload URLs, campaign domains, specific profile URLs in the observed chain, and public IPs embedded in payload URLs. It suppresses known fetch error body hashes and routine background infrastructure.

## MegaRigger example

The MegaRigger report provided a clear validation case:

https://tria.ge/260731-d39sdsam7z/behavioral1

Observed source URL:

\`https://github.com/MegaRiggerRouse/venhdusk/releases/download/sjnvhkd/SoftwareSetup.zip\`

The tria.ge task scored 10. Reported behavior included PowerShell execution, Windows security bypass behavior, Microsoft Defender exclusion through PowerShell, dropped EXE execution, dropped DLL loading, hidden window behavior, Windows Script Host and VBScript execution, obfuscated script activity, and executable retrieval.

![MegaRigger lure chain grounded in tria.ge and PCAP evidence](${require("./article_pics/grift_megarigger_flow_improved.png")})

Payload retrieval URL:

\`http://192.162.199.149/uploads/a288d2a8a88442f3a0032712516b978a.exe\`

The embedded public IP was promoted as a standalone indicator because it appeared inside the executable retrieval path. A VirusTotal check at analysis time showed 19 of 91 vendors flagging the IP with a community score of minus 12.

The GitHub release URL explains the lure path. The tria.ge behavior establishes malicious execution context. The extracted payload URL and public IP provide durable indicator value.

## PCAP evidence

The packet capture used here was downloaded from the tria.ge report by the analyst, not collected by GRIFT. That distinction matters. GRIFT records the tria.ge report context and final indicators. The PCAPNG remains an optional evidence artifact available from tria.ge when packet level confirmation is needed.

In this case, the tria.ge behavioral PCAP confirmed the network sequence. The sandbox host requested an executable from 192.162.199.149 and received an octet stream response.

![Wireshark display filter showing the HTTP payload request and response](${require("./article_pics/grift_wireshark_payload_http_filter.png")})

Key frames:

- frame 103775: HTTP GET to \`http://192.162.199.149/uploads/a288d2a8a88442f3a0032712516b978a.exe\`;
- frame 107413: HTTP 200 OK, \`application/octet-stream\`, content length \`3342688\`, server \`nginx/1.10.3\`.

![PCAP payload download sequence](${require("./article_pics/grift_pcap_payload_download_sequence.png")})

The top TCP conversations also explain reporting choices. GitHub release asset traffic was large and relevant to delivery context. The stronger blocking indicator was the executable retrieval path on 192.162.199.149 rather than bare GitHub platform infrastructure.

![Wireshark TCP conversations sorted by bytes](${require("./article_pics/grift_wireshark_tcp_conversations_sorted.png")})

## Final report shape

The primary GRIFT outputs are the candidate report and the final indicator files. The reviewed final output covered 2 app groups, 3 found repository groups, and 9 tria.ge sample IDs. The final report grouped results by app and repository so downstream processing can preserve lure context instead of flattening every value into an unlabeled indicator list.

App and repository groups:

- Audacity
  - Audacity Audio Editing / Audacity Windows Download
- TeamViewer
  - Virtualflupierce92 / TeamViewer Activator 2026
  - splitspongechannel / TeamViewer Activator Latest

The final report kept high scoring executable hashes, payload URLs, campaign domains, and selected Telegram or Steam profile URLs tied to the observed chain. It did not promote bare GitHub platform domains, temporary release asset redirects, Microsoft or browser background traffic, certificate services, IP check services, or fetch error body hashes.

The reporting goal is a concise analyst package, not a dump of every sandbox observation. The JSON output is structured enough for modification scripts that adapt GRIFT findings to the defended environment. Common examples include deduplicated CSV exports, Splunk lookup tables, SIEM watchlists, firewall or proxy block lists, DNS control updates, and ticket enrichment. Those adapters can preserve local policy choices without changing the core GRIFT workflow.

## Running GRIFT

GRIFT can run as an analyst driven tool or as a scheduled collection job. A weekly cron job or systemd timer can search for newly created lure repositories, enrich candidate payload URLs, check tria.ge for existing reports, and write fresh indicators for review. That cadence helps surface unreported or newly staged infrastructure before it is widely blocked. In operational terms, scheduled GRIFT runs can reduce exposure to fresh exploit delivery and shorten Mean Time To Detect (MTTD).

Initial setup follows the repository README. The setup creates the workspace directories, a gitignored local environment file, and the seed files used for target management:

\`\`\`bash
cd /path/to/GRIFT
python3 -m venv .venv
. .venv/bin/activate
pip install -r requirements.txt
python grift.py --init
python grift.py --set-github-token
python grift.py --set-triage-key
\`\`\`

GITHUB_TOKEN supports reliable GitHub API search and rate limits. TRIAGE_KEY is only needed when Stage 2 tria.ge options are used. GRIFT stores keys in the local .env file, masks them in output, and validates required keys before a run.

Searches use brands.yaml as the active target list. If input/apps.txt is changed, rebuild brands.yaml before searching:

\`\`\`bash
printf 'brands: []\ndefaults:\n  min_score_report: 4\n  per_query_results: 20\n' > brands.yaml && python grift.py --import-apps input/apps.txt && python grift.py --list-brands
\`\`\`

The recommended full read only run performs GitHub search, candidate scoring, payload URL enrichment, and tria.ge lookup without submitting samples:

\`\`\`bash
python grift.py --skip-app-review --created-after 2026-07-01 --min-score 4 --enrich-urls --triage-lookup --triage-min-score 8 --triage-max-urls 3 --triage-timeout 10 --out out/run-$(date -u +%Y%m%dT%H%M%SZ)
\`\`\`

The threshold values in that example match the testing run. \`--created-after 2026-07-01\` keeps the search focused on fresh repositories. \`--min-score 4\` is the candidate reporting and URL-enrichment threshold; candidates below 4 stay out of the main Markdown report and are not URL-enriched by \`--enrich-urls\`. \`--triage-min-score 8\` limits read only tria.ge lookup to the strongest candidates. \`--triage-max-urls 3\` checks up to three payload URLs per candidate. \`--triage-timeout 10\` gives each tria.ge API request ten seconds before the run moves on.

For a Stage 1 only candidate queue, run GitHub search and reporting without tria.ge:

\`\`\`bash
python grift.py --created-after 2026-07-01 --min-score 4 --out out/run-$(date -u +%Y%m%dT%H%M%SZ)
\`\`\`

For a Stage 1 variant with payload URL probing, add enrichment:

\`\`\`bash
python grift.py --skip-app-review --created-after 2026-07-01 --min-score 4 --enrich-urls --out out/run-$(date -u +%Y%m%dT%H%M%SZ)
\`\`\`

For a single configured target, use the brand filter:

\`\`\`bash
python grift.py --brand Audacity --created-after 2026-07-01
\`\`\`

For scheduled collection, use cron mode. Cron mode disables prompts and requires GITHUB_TOKEN:

\`\`\`bash
python grift.py --cron --created-after 2026-07-01 --out out/cron-latest
\`\`\`

Normal run outputs include \`candidates_latest.json\`, \`candidates_latest.csv\`, \`report_latest.md\`, \`final_iocs_latest.json\`, and \`final_iocs_latest.txt\`. The report is the roll up summary. The candidates JSON preserves structured lookup data. The final IoC files are the analyst facing deliverable and only include repositories with dangerous looking tria.ge report output after routine browser, operating system, certificate, CDN, and public IP check traffic is suppressed. PCAPNG files are not a GRIFT output; they can be downloaded from tria.ge when packet evidence is needed.

## Reporting boundary

GRIFT is a GitHub SEO malware hunting workflow. It identifies suspicious fake software delivery infrastructure, ranks candidate repositories, connects selected candidates to sandbox behavior, and exports indicators with platform noise removed.

That boundary keeps the reporting precise. Repository shape supports a hunting lead. tria.ge behavior supports malicious execution context. Analyst downloaded PCAP evidence can support payload retrieval. Final IoC output carries the indicators that remain useful after that separation, and local adapter scripts can translate those indicators into the formats required by a specific defended environment.
`,
};

export default article;
